"use client";

import * as Headless from "@headlessui/react";
import clsx from "clsx";
import { useState } from "react";

/**
 * The properties for {@link Combobox}
 */
export type ComboboxProps<T> = {
    /** The list of options to filter and display */
    options: T[];
    /** Function to get the display string for an option */
    displayValue: (value: T | null) => string | undefined;
    /** Custom filter function for options */
    filter?: (value: T, query: string) => boolean;
    /** Which options cannot be picked and are skipped by the keyboard, such as a {@link ComboboxHeading} */
    optionDisabled?: (value: T) => boolean;
    /** Additional CSS classes */
    className?: string;
    /** Placeholder text for the input */
    placeholder?: string;
    /** Whether to auto-focus the input */
    autoFocus?: boolean;
    /** Accessible label for the combobox */
    "aria-label"?: string;
    /** Render function for each option */
    children: (value: NonNullable<T>) => React.ReactElement;
    /** Anchor position of the dropdown */
    anchor?: "top" | "bottom";
    /**
     * Callback with the current query string. Use this for
     * async/server-side filtering: update the `options` prop
     * from the parent.
     */
    onQueryChange?: (query: string) => void;
} & Omit<Headless.ComboboxProps<T, false>, "as" | "multiple" | "children">;

/**
 * A searchable select dropdown - note that this is our own custom version
 * before catalyst UI had this, so it's a bit different from upstream.
 *
 * ## Client-side filtering
 *
 * By default the component filters `options` in-memory on every keystroke.
 * It matches against the string returned by `displayValue`. Pass a custom
 * `filter` function to override the match logic (e.g. fuzzy search, field
 * matching).
 *
 * ```tsx
 * <Combobox
 *   options={users}
 *   displayValue={(user) => user?.name}
 *   filter={(user, q) => user.name.toLowerCase().startsWith(q.toLowerCase())}
 *   onChange={setSelectedUser}
 * >
 *   {(user) => (
 *     <ComboboxOption value={user}>
 *       <ComboboxLabel>{user.name}</ComboboxLabel>
 *     </ComboboxOption>
 *   )}
 * </Combobox>
 * ```
 *
 * ## Server-side filtering
 *
 * Pass `onQueryChange` to react to the search input and then update
 * `options` from the parent (e.g. after a debounced API call). While the
 * request is in-flight the `filter` prop can still narrow the stale result
 * set so the list does not feel empty/unresponsive during loading.
 *
 * ```tsx
 * <Combobox
 *   options={results}
 *   displayValue={(user) => user?.name}
 *   onQueryChange={(q) => debouncedFetch(q)}
 *   filter={(user, q) => user.name.toLowerCase().includes(q.toLowerCase())}
 *   onChange={setSelectedUser}
 * >
 *   {(user) => (
 *     <ComboboxOption value={user}>
 *       <ComboboxLabel>{user.name}</ComboboxLabel>
 *     </ComboboxOption>
 *   )}
 * </Combobox>
 * ```
 *
 * @see https://catalyst.tailwindui.com/docs/combobox
 */
export function Combobox<T>(props: ComboboxProps<T>) {
    const {
        options,
        displayValue,
        filter,
        optionDisabled,
        anchor = "bottom",
        className,
        placeholder,
        autoFocus,
        "aria-label": ariaLabel,
        children,
        onQueryChange,
        ...rest
    } = props;
    const [query, setQuery] = useState("");

    const filteredOptions =
        query === ""
            ? options
            : options.filter((option) =>
                  filter ? filter(option, query) : displayValue(option)?.toLowerCase().includes(query.toLowerCase()),
              );

    return (
        <Headless.Combobox
            {...rest}
            multiple={false}
            virtual={{ options: filteredOptions, disabled: optionDisabled }}
            onClose={() => {
                setQuery("");
                onQueryChange?.("");
            }}
        >
            <span
                data-slot="control"
                className={clsx([
                    className,
                    // Basic layout
                    "relative block w-full",
                    // Background color + shadow applied to inset pseudo element, so shadow blends with border in light mode
                    "before:absolute before:inset-px before:rounded-[calc(var(--radius-lg)-1px)] before:bg-white before:shadow-sm",
                    // Background color is moved to control and shadow is removed in dark mode so hide `before` pseudo
                    "dark:before:hidden",
                    // Focus ring
                    "after:pointer-events-none after:absolute after:inset-0 after:rounded-lg after:ring-transparent after:ring-inset sm:focus-within:after:ring-2 sm:focus-within:after:ring-blue-500",
                    // Disabled state
                    "has-data-disabled:opacity-50 has-data-disabled:before:bg-zinc-950/5 has-data-disabled:before:shadow-none",
                    // Invalid state
                    "has-data-invalid:before:shadow-red-500/10",
                ])}
            >
                <Headless.ComboboxInput
                    autoFocus={autoFocus}
                    data-slot="control"
                    aria-label={ariaLabel}
                    displayValue={(option: T) => displayValue(option) ?? ""}
                    onChange={(event) => {
                        setQuery(event.target.value);
                        onQueryChange?.(event.target.value);
                    }}
                    placeholder={placeholder}
                    className={clsx([
                        className,
                        // Basic layout
                        "relative block w-full appearance-none rounded-lg py-[calc(--spacing(2.5)-1px)] sm:py-[calc(--spacing(1.5)-1px)]",
                        // Horizontal padding
                        "pr-[calc(--spacing(10)-1px)] pl-[calc(--spacing(3.5)-1px)] sm:pr-[calc(--spacing(9)-1px)] sm:pl-[calc(--spacing(3)-1px)]",
                        // Typography
                        "text-base/6 text-zinc-950 placeholder:text-zinc-500 sm:text-sm/6 dark:text-white",
                        // Border
                        "border border-zinc-950/10 data-hover:border-zinc-950/20 dark:border-white/10 dark:data-hover:border-white/20",
                        // Background color
                        "bg-transparent dark:bg-white/5",
                        // Hide default focus styles
                        "focus:outline-hidden",
                        // Invalid state
                        "data-invalid:border-red-500 data-invalid:data-hover:border-red-500 dark:data-invalid:border-red-500 dark:data-invalid:data-hover:border-red-500",
                        // Disabled state
                        "data-disabled:border-zinc-950/20 dark:data-disabled:border-white/15 dark:data-disabled:bg-white/2.5 dark:data-hover:data-disabled:border-white/15",
                        // System icons
                        "dark:scheme-dark",
                    ])}
                />
                <Headless.ComboboxButton className="group absolute inset-y-0 right-0 flex items-center px-2">
                    <svg
                        className="size-5 stroke-zinc-500 group-data-disabled:stroke-zinc-600 group-data-hover:stroke-zinc-700 sm:size-4 dark:stroke-zinc-400 dark:group-data-hover:stroke-zinc-300 forced-colors:stroke-[CanvasText]"
                        viewBox="0 0 16 16"
                        aria-hidden="true"
                        fill="none"
                    >
                        <path
                            d="M5.75 10.75L8 13L10.25 10.75"
                            strokeWidth={1.5}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                        <path
                            d="M10.25 5.25L8 3L5.75 5.25"
                            strokeWidth={1.5}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </Headless.ComboboxButton>
            </span>
            <Headless.ComboboxOptions
                transition
                anchor={anchor}
                className={clsx(
                    // Anchor positioning
                    "[--anchor-gap:--spacing(2)] [--anchor-padding:--spacing(4)] sm:data-[anchor~=start]:[--anchor-offset:-4px]",
                    // Two thirds of the screen at most on a phone, so the page
                    // behind the menu never disappears entirely.
                    "max-sm:[--anchor-max-height:66vh]",
                    // Base styles,
                    "isolate min-w-[calc(var(--input-width)+8px)] scroll-py-1 rounded-xl p-1 select-none empty:invisible",
                    // Invisible border that is only visible in `forced-colors` mode for accessibility purposes
                    "outline outline-transparent focus:outline-hidden",
                    // Handle scrolling when menu won't fit in viewport
                    "overflow-y-scroll overscroll-contain",
                    // Popover background
                    "bg-white/75 backdrop-blur-xl dark:bg-zinc-800/75",
                    // Shadows
                    "shadow-lg ring-1 ring-zinc-950/10 dark:ring-white/10 dark:ring-inset",
                    // Transitions
                    "transition-opacity duration-100 ease-in data-closed:data-leave:opacity-0 data-transition:pointer-events-none",
                )}
            >
                {({ option }) => children(option)}
            </Headless.ComboboxOptions>
        </Headless.Combobox>
    );
}

/**
 * The properties for {@link ComboboxOption}
 */
export type ComboboxOptionProps<T> = {
    /** Additional CSS classes */
    className?: string;
    /** The option content */
    children?: React.ReactNode;
} & Omit<Headless.ComboboxOptionProps<"div", T>, "as" | "className">;

/**
 * An option within a {@link Combobox}
 *
 * @see https://catalyst.tailwindui.com/docs/combobox
 */
export function ComboboxOption<T>(props: ComboboxOptionProps<T>) {
    const { children, className, ...rest } = props;
    const sharedClasses = clsx(
        // Base
        "flex min-w-0 items-center",
        // Icons
        "*:data-[slot=icon]:size-5 *:data-[slot=icon]:shrink-0 sm:*:data-[slot=icon]:size-4",
        "*:data-[slot=icon]:text-zinc-500 group-data-focus/option:*:data-[slot=icon]:text-white dark:*:data-[slot=icon]:text-zinc-400",
        "forced-colors:*:data-[slot=icon]:text-[CanvasText] forced-colors:group-data-focus/option:*:data-[slot=icon]:text-[Canvas]",
        // Avatars
        "*:data-[slot=avatar]:-mx-0.5 *:data-[slot=avatar]:size-6 sm:*:data-[slot=avatar]:size-5",
    );

    return (
        <Headless.ComboboxOption
            {...rest}
            className={clsx(
                // Basic layout
                "group/option grid w-full cursor-default grid-cols-[1fr_--spacing(5)] items-baseline gap-x-2 rounded-lg py-2.5 pr-2 pl-3.5 sm:grid-cols-[1fr_--spacing(4)] sm:py-1.5 sm:pr-2 sm:pl-3",
                // Typography
                "text-base/6 text-zinc-950 sm:text-sm/6 dark:text-white forced-colors:text-[CanvasText]",
                // Focus
                "outline-hidden data-focus:bg-blue-500 data-focus:text-white",
                // Forced colors mode
                "forced-color-adjust-none forced-colors:data-focus:bg-[Highlight] forced-colors:data-focus:text-[HighlightText]",
                // Disabled
                "data-disabled:opacity-50",
            )}
        >
            <span className={clsx(className, sharedClasses)}>{children}</span>
            <svg
                className="relative col-start-2 hidden size-5 self-center stroke-current group-data-selected/option:inline sm:size-4"
                viewBox="0 0 16 16"
                fill="none"
                aria-hidden="true"
            >
                <path d="M4 8.5l3 3L12 4" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        </Headless.ComboboxOption>
    );
}

/**
 * The properties for {@link ComboboxHeading}
 */
export type ComboboxHeadingProps<T> = {
    /** Additional CSS classes */
    className?: string;
    /** The heading text */
    children?: React.ReactNode;
} & Omit<Headless.ComboboxOptionProps<"div", T>, "as" | "className" | "disabled">;

/**
 * A heading over a group of options within a {@link Combobox}
 *
 * One of the `options` like any other, so it takes a `value` of its own and
 * is listed where the group begins; the Combobox's `optionDisabled` has to name it
 * so it is neither picked nor reached by the keyboard.
 */
export function ComboboxHeading<T>(props: ComboboxHeadingProps<T>) {
    const { children, className, ...rest } = props;
    return (
        <Headless.ComboboxOption
            {...rest}
            disabled={true}
            className={clsx(
                className,
                "block w-full cursor-default px-3.5 pt-2 pb-1 text-sm/5 font-medium text-zinc-500 select-none sm:px-3 sm:text-xs/5 dark:text-zinc-400",
            )}
        >
            {children}
        </Headless.ComboboxOption>
    );
}

/**
 * The text label of a {@link ComboboxOption}
 */
export function ComboboxLabel(props: React.ComponentPropsWithoutRef<"span">) {
    const { className, ...rest } = props;
    return <span {...rest} className={clsx(className, "ml-2.5 truncate first:ml-0 sm:ml-2 sm:first:ml-0")} />;
}

/**
 * A secondary description within a {@link ComboboxOption}
 */
export function ComboboxDescription(props: React.ComponentPropsWithoutRef<"span">) {
    const { className, children, ...rest } = props;
    return (
        <span
            {...rest}
            className={clsx(
                className,
                "flex flex-1 overflow-hidden text-zinc-500 group-data-focus/option:text-white before:w-2 before:min-w-0 before:shrink dark:text-zinc-400",
            )}
        >
            <span className="flex-1 truncate">{children}</span>
        </span>
    );
}
